import { queryStatus, TaskStatus } from '../model';
import { action, makeObservable, observable, runInAction } from 'mobx';

type TaskFunc<T> = () => Promise<T>;
type TaskErrorFunc = (error: unknown) => unknown;

export class TaskState<T> {
  status: TaskStatus<T, unknown> = {
    ...queryStatus.idle,
  };

  _fn?: TaskFunc<T>;
  _onError?: TaskErrorFunc;

  constructor() {
    makeObservable(this, {
      status: observable.ref,
      run: action,
      runOnce: action,
      reset: action,
    });
  }

  runOnce(fn?: TaskFunc<T>, onError?: TaskErrorFunc) {
    if (fn) {
      this._fn = fn;
    }

    if (onError) {
      this._onError = onError;
    }

    if (!this._fn) {
      return;
    }

    const { status } = this;

    if (status.isLoading || status.isSuccess) {
      return;
    }

    return this.run(this._fn, this._onError);
  }

  get errorStatus() {
    const { status } = this;
    return status.isError ? status : undefined;
  }

  reset() {
    this.status = {
      ...queryStatus.idle,
    };
  }

  async run(fn: TaskFunc<T>, onError?: TaskErrorFunc) {
    const promise = fn();
    this.status = {
      ...queryStatus.loading,
      promise,
    };

    try {
      const data = await promise;
      runInAction(() => {
        if (promise === this.status.promise) {
          this.status = {
            ...queryStatus.success,
            error: null,
            data,
          };
        }
      });
    } catch (err) {
      onError?.(err);
      const error = err;
      runInAction(() => {
        if (promise === this.status.promise) {
          this.status = {
            ...queryStatus.error,
            error,
          };
        }
      });
    }
  }
}
